/* eslint-disable no-case-declarations */
import React from 'react';
import { PlayerMatchupTypes, PitchingDecisionTypes } from '@mlb/react-patterns/v4/components/PlayerMatchup/playerMatchup.types';
import PlayerMatchup from '@mlb/react-patterns/v4/components/PlayerMatchup/playerMatchup';
import GameStateTypes from '../game/types/GameStateTypes';
import { PlayerStatsContentWrapper, PlayerStatsSeparator } from '../game/components/PlayerMatchups/PlayerMatchups.style';
import getTranslation from './getTranslation';
import { cardTypes } from './productButtonConfigs';
import {
    battingNextString,
    gameDataString,
    inTheHoleString,
    liveBatterString,
    livePitcherString,
    losingPitcherString,
    onDeckString,
    previewAwayPitcher,
    previewHomePitcher,
    savingPitcherString,
    winningPitcherString
} from '../game/components/PlayerMatchupLayer/utils/playerMatchupAriaLabel';

export const PITCHING = 'pitching';
const HITTING = 'hitting';
const LEFT = 'left';
const ERA = 'era';

const generatePlayerStats = (player, stats = {}, dictionary, type, index, simpleInline, separator = '', dueUp) => {
    const { boxscoreName } = player;
    // if no name use default
    if (!boxscoreName) {
        return <PlayerStatsContentWrapper>-</PlayerStatsContentWrapper>;
    }

    const { statsSingleSeason = {}, gameLog = {} } = stats;
    const { wins, losses, era, saves, ops } = statsSingleSeason;
    const { hits, atBats, inningsPitched } = gameLog;

    switch (type) {
        case PlayerMatchupTypes.PITCHERPREGAME:
            const record = `${wins} - ${losses}`;
            const formattedRecord = simpleInline ? `(${record})` : record;
            return (
                <React.Fragment>
                    <PlayerStatsContentWrapper>{ formattedRecord }</PlayerStatsContentWrapper>
                    { !simpleInline && <PlayerStatsContentWrapper><PlayerStatsSeparator>{separator}</PlayerStatsSeparator>{era} {getTranslation(dictionary, ERA, 'ERA')}</PlayerStatsContentWrapper> }
                </React.Fragment>
            );
        case PlayerMatchupTypes.PITCHERLIVE:
            const pitcherStats = (
                <React.Fragment>
                    <PlayerStatsContentWrapper>{inningsPitched} {getTranslation(dictionary, 'innings_pitched_abbrev', 'IP')} </PlayerStatsContentWrapper>
                    { <PlayerStatsContentWrapper><PlayerStatsSeparator>{separator}</PlayerStatsSeparator>{era} {getTranslation(dictionary, ERA, 'ERA')}</PlayerStatsContentWrapper> }
                </React.Fragment>
            );
            return simpleInline
                ? (
                    <React.Fragment>
                    ({ pitcherStats })
                    </React.Fragment>
                )
                : pitcherStats;
        case PlayerMatchupTypes.PITCHERPOSTGAME:
            const pitcherRecord = `${wins} - ${losses}`;
            const postgameFormattedRecord = simpleInline ? `(${pitcherRecord})` : pitcherRecord;
            const formattedSaves = simpleInline ? `(${saves})` : saves;
            const validRecord = (typeof wins !== 'undefined' || typeof losses !== 'undefined') ? postgameFormattedRecord : ''; // don't display record if wins or losses are undefined

            return (
                <React.Fragment>
                    { index !== 2 && <PlayerStatsContentWrapper>{validRecord}</PlayerStatsContentWrapper> }
                    { index !== 2 && !simpleInline && validRecord && <PlayerStatsContentWrapper><PlayerStatsSeparator>{separator}</PlayerStatsSeparator>{era} {getTranslation(dictionary, ERA, 'ERA')}</ PlayerStatsContentWrapper> }
                    { index === 2 && <PlayerStatsContentWrapper>{formattedSaves}</PlayerStatsContentWrapper> }
                </React.Fragment>
            );
        case PlayerMatchupTypes.HITTERLIVE:
            const hitterStats = (
                <React.Fragment>
                    <PlayerStatsContentWrapper>{hits} - {atBats}</PlayerStatsContentWrapper>
                    { !dueUp && <PlayerStatsContentWrapper><PlayerStatsSeparator>{separator}</PlayerStatsSeparator>{ops} {getTranslation(dictionary, 'ops', 'OPS')}</PlayerStatsContentWrapper> }
                </React.Fragment>
            );
            return simpleInline
                ? (
                    <React.Fragment>
                    ({ hitterStats })
                    </React.Fragment>
                )
                : hitterStats;
        default:
    }
};

// playerType is "hitting" or "pitching"
const selectStats = (statsArr, playerType) => {
    if (!statsArr) {
        return {};
    }
    const playerStats = { gameLog: null, statsSingleSeason: null };
    // TODO array has season and game stats. Might be nice to do some of this work on the model
    statsArr.forEach(({ group, type, stats }) => {
        const statType = type.displayName;
        if (group.displayName === playerType && !playerStats[statType]) {
            playerStats[statType] = stats;
        }
    });

    return playerStats;
};

export const buildPlayerUrl = (urlRoot, nameSlug = '') => `${urlRoot}/player/${nameSlug}`;

export const generateMatchups = ({
    homeTeam,
    awayTeam,
    dictionary,
    gameState,
    linescore,
    decisions,
    isLoading,
    urlRoot,
    headshotTypes,
    abstract,
    simpleInline,
    component,
    dueUpConfig = {},
    showHeadShot = true,
    playerStatsInline = false,
    mode = cardTypes.SCOREBOARD,
    namePlaceholder = '-',
    disablePlayerPageLink,
    enableMatchupPitcherTBD = true
}) => {
    const PlayerMatchupComponent = component || PlayerMatchup;
    const headshotZoomScale = headshotTypes === 'wbc' ? 1 : undefined;
    let playerArr = [];
    let typesArr = [];
    let statsArr = [];
    const {
        isPostponed,
        isCancelled,
        isSuspended,
        isWarmup
    } = abstract;

    const isLive = gameState === GameStateTypes.LIVE;
    const {
        offense = {},
        defense
    } = linescore;

    let stacked = false;
    const dueUp = dueUpConfig.isBetweenInnings && dueUpConfig.hasCompletePlayerData;

    let playerInfoString = '';
    const awayTeamShortName = (awayTeam && awayTeam.team && awayTeam.team.shortName) || '';
    const homeTeamShortName = (homeTeam && homeTeam.team && homeTeam.team.shortName) || '';

    const offenseTeamName = linescore?.inningState === 'Top' || linescore?.inningState === 'End' ? awayTeamShortName : homeTeamShortName;
    const defenseTeamName = linescore?.inningState === 'Top' || linescore?.inningState === 'End' ? homeTeamShortName : awayTeamShortName;

    if (gameState === GameStateTypes.PREVIEW || isWarmup) {
        const awayPitcher = awayTeam.probablePitcher;
        const homePitcher = homeTeam.probablePitcher;

        // if both pitchers are tbd && simpleInline then do not render anything

        if (simpleInline && !awayPitcher && !homePitcher && !enableMatchupPitcherTBD) {
            return [];
        }

        playerArr = [awayPitcher, homePitcher];
        typesArr = [PlayerMatchupTypes.PITCHERPREGAME, PlayerMatchupTypes.PITCHERPREGAME];
        if ((awayPitcher && awayPitcher.stats) || (homePitcher && homePitcher.stats)) {
            statsArr = [selectStats(awayPitcher && awayPitcher.stats, PITCHING), selectStats(homePitcher && homePitcher.stats, PITCHING)];
        }
    } else if (dueUp) {
        const {
            batter,
            onDeck,
            inHole
        } = dueUpConfig;
        playerArr = [batter, onDeck, inHole];
        typesArr = [PlayerMatchupTypes.HITTERLIVE, PlayerMatchupTypes.HITTERLIVE, PlayerMatchupTypes.HITTERLIVE];
        statsArr = playerArr.map((player) => (selectStats(player.stats, HITTING)));
        stacked = !simpleInline;
    } else if (isLive) {
        // if simpleInline && suspended render nothing
        if (simpleInline && isSuspended) {
            return [];
        }

        const pitcher = defense.pitcher;
        const hitter = offense.batter;
        playerArr = [pitcher, hitter];
        typesArr = [PlayerMatchupTypes.PITCHERLIVE, PlayerMatchupTypes.HITTERLIVE];
        if ((pitcher && pitcher.stats) && (hitter && hitter.stats)) {
            statsArr = [selectStats(pitcher.stats, PITCHING), selectStats(hitter.stats, HITTING)];
        }
    } else {
        // if simpleInline (schedule view) && cancelled or postponed or no decisions (tied) render nothing
        if (simpleInline && (isPostponed || isCancelled || !decisions)) {
            return [];
        }

        playerArr = decisions ? Object.values(decisions) : [{}, {}];
        typesArr = [PlayerMatchupTypes.PITCHERPOSTGAME, PlayerMatchupTypes.PITCHERPOSTGAME, PlayerMatchupTypes.PITCHERPOSTGAME];
        statsArr = playerArr.map((player) => (selectStats(player.stats, PITCHING)));
    }

    return playerArr.map((player, i) => {
        const alignment = LEFT;
        let decision;

        try {
            if (gameState === GameStateTypes.PREVIEW || isWarmup || isPostponed) {
                playerInfoString = player?.id === awayTeam?.probablePitcher?.id ? previewAwayPitcher(awayTeam, isPostponed, dictionary) : previewHomePitcher(homeTeam, isPostponed, dictionary);
            } else if (dueUp) {
                playerInfoString = i < 1 ? battingNextString(dictionary, offenseTeamName, dueUpConfig) : i === 1 ? onDeckString(dictionary, offenseTeamName, dueUpConfig) : inTheHoleString(dictionary, offenseTeamName, dueUpConfig);
            } else if (isLive) {
                playerInfoString = i < 1 ? livePitcherString(dictionary, defenseTeamName, defense) : liveBatterString(dictionary, offenseTeamName, offense, linescore) + gameDataString(dictionary, linescore);
            } else if (gameState === GameStateTypes.FINAL && !isPostponed) {
                playerInfoString = i < 1 ? winningPitcherString(dictionary, decisions) : i === 1 ? losingPitcherString(dictionary, decisions) : savingPitcherString(dictionary, decisions);
            } else {
                playerInfoString = '';
            }
        } catch (error) {
            console.error('Error setting playerInfoString:', error);
            playerInfoString = '';
        }

        if (!(gameState === GameStateTypes.PREVIEW || isPostponed) && !(isLive)) {
            decision = i === 0
                ? PitchingDecisionTypes.W
                : i === 1
                    ? PitchingDecisionTypes.L
                    : (mode === cardTypes.MINI_SB)
                        ? PitchingDecisionTypes.SV
                        : PitchingDecisionTypes.S;
        }

        const headshotWidth = gameState === GameStateTypes.FINAL
            ? '29px'
            : dueUp
                ? '24px' // needs to be smaller for limited space on scores desktop player matchup area
                : '33px';
        const noNameLabel = (gameState === GameStateTypes.PREVIEW || isPostponed)
            ? getTranslation(dictionary, 'game_time_tbd', 'TBD')
            : namePlaceholder;

        if (!player || isPostponed) {
            return (
                <div key={i} aria-label={playerInfoString} tabIndex={'0'}>
                    <PlayerMatchupComponent
                        type={typesArr[i]}
                        alignment={alignment}
                        isLoading={isLoading}
                        headshotWidth={headshotWidth}
                        headshotTypes={headshotTypes}
                        noNameLabel={noNameLabel}
                        simpleInline={simpleInline}
                        playerStats={simpleInline ? '' : undefined}
                        className="sns"
                        showHeadShot={showHeadShot}
                        playerStatsInline={playerStatsInline}
                        mode={mode}
                        headshotZoomScale={headshotZoomScale}
                    />
                </div>
            );
        }

        const { id, boxscoreName, nameSlug, pitchHand = {} } = player;
        const playerStats = generatePlayerStats(player, statsArr[i], dictionary, typesArr[i], i, simpleInline, '|', dueUp);
        return (
            <PlayerMatchupComponent
                key={`player-${i}-${id}`}
                playerId={id}
                name={boxscoreName}
                linkProps={disablePlayerPageLink ? undefined : { url: buildPlayerUrl(urlRoot, nameSlug), ariaLabel: playerInfoString }}
                playerStats={playerStats}
                type={typesArr[i]}
                pitchHand={pitchHand.code}
                alignment={alignment}
                decision={getTranslation(dictionary, decision, decision)}
                isLoading={isLoading}
                headshotWidth={headshotWidth}
                headshotType={headshotTypes}
                noNameLabel={noNameLabel}
                simpleInline={simpleInline}
                className='sns'
                stacked={stacked}
                showHeadShot={showHeadShot}
                playerStatsInline={playerStatsInline}
                mode={mode}
                headshotZoomScale={headshotZoomScale}
            />
        );
    });
};
